import { jsx } from 'react/jsx-runtime';
import { useContext, createContext, useState, useCallback, useMemo } from 'react';
import { setSummarizeText } from '@fabric-app/compat';
import { useQueryUserInfo } from '../../../user/queries/useQueryUserInfo.js';
import { noop } from 'lodash-es';
import { useExpandedResourceState, useShowExpandedResource } from '../../../resources/stores/useExpandedResourceStore.js';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useGlobalSearchSetValue, defaultSearchBarValue } from '../../../search/hooks/hybridsearch.js';
import { useSearchOverlay } from '../../../search/hooks/useSearchOverlay.js';
import { useFabricEvent, dispatchFabricEvent } from '../../../hooks/useFabricEvent.js';
import { useMutationCreateComment } from '../../../comments/mutations/useMutationCreateComment.js';
import { isNumber, isString, isDefined } from '@fabric-app/utils/guards';
import { useQueryComments } from '../../../comments/queries/useQueryComments.js';

const ExpandedFdocContext = /* @__PURE__ */ createContext({
  selectedExtraPanel: null,
  setSelectedExtraPanel: noop,
  sidebarAnimateIn: true,
  setSimilarText: noop,
  sidebarOpen: false,
  setSidebarOpen: noop,
  editTagsModalOpen: false,
  setEditTagsModalOpen: noop,
  shareModalOpen: false,
  setShareModalOpen: noop,
  handleOnDelete: noop,
  pdf: null,
  setPdf: noop,
  pagesTextContent: null,
  toolbarHeight: 0,
  setToolbarHeight: noop,
  isEditorFocused: false,
  setIsEditorFocused: noop,
  onGetSimilarItemsToText: noop,
  onSummarizeText: noop,
  isAnon: false,
  hideBottomBar: false,
  setHideBottomBar: noop,
  hideTopBar: false,
  setHideTopBar: noop,
  annotationData: null,
  setAnnotationData: noop,
  focusCommentId: null,
  setFocusCommentId: noop,
  bottomAdditionalHeight: 0,
  setBottomAdditionalHeight: noop
});
const ExpandedFdocProvider = ({ children, overrideSidebarOpen, setOverrideSidebarOpen, handleOnDelete, resourceId, handleClose, bottomAdditionalHeight = 0, setBottomAdditionalHeight = noop }) => {
  const { data: user } = useQueryUserInfo();
  const { expandedResourceSidebarOpen, setExpandedResourceSidebarOpen, expandedResourceSidebarTab, setExpandedResourceSidebarTab, expandedResourceSidebarInitTab } = useExpandedResourceState();
  const [similarText, setSimilarText] = useState(void 0);
  const [editTagsModalOpen, setEditTagsModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const [focusCommentId, setFocusCommentId] = useState(null);
  const [pdf, _setPdf] = useState(null);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [pagesTextContent, setPagesTextContent] = useState(null);
  const [hideBottomBar, setHideBottomBar] = useState(false);
  const [hideTopBar, setHideTopBar] = useState(false);
  const [annotationData, setAnnotationData] = useState(null);
  const setPdf = useCallback((pdf2) => {
    _setPdf((oldPdf) => {
      oldPdf?.destroy();
      return pdf2;
    });
    const processPages = async () => {
      const pageCount = pdf2.numPages;
      const pagePromises = Array.from({
        length: pageCount
      }, (_, index) => pdf2.getPage(index + 1).then((pageData) => pageData.getTextContent()).then((textContent) => textContent.items.map((item) => "str" in item ? item.str : "").join(" ")));
      const pages = await Promise.all(pagePromises);
      setPagesTextContent(pages);
    };
    processPages();
  }, []);
  const { openOverlay } = useSearchOverlay();
  const showExpandedResource = useShowExpandedResource();
  const setSearchBarValue = useGlobalSearchSetValue();
  const { isMobileView } = useResponsive();
  const onGetSimilarItemsToText = useCallback((text) => {
    if (isMobileView) {
      setExpandedResourceSidebarOpen(true);
      setExpandedResourceSidebarTab("similar");
      setSimilarText(text);
    } else {
      setSearchBarValue({
        ...defaultSearchBarValue,
        query: text
      });
      openOverlay("global", {
        onCloseOverlayOnce: () => {
          showExpandedResource(resourceId);
          setSearchBarValue((prev) => ({
            ...prev,
            query: ""
          }));
        }
      });
      handleClose?.();
    }
  }, [
    setExpandedResourceSidebarOpen,
    setExpandedResourceSidebarTab,
    isMobileView,
    resourceId,
    setSearchBarValue,
    showExpandedResource,
    openOverlay,
    handleClose
  ]);
  const onSummarizeText = useCallback((text) => {
    setExpandedResourceSidebarOpen(true);
    setExpandedResourceSidebarTab("assistant");
    setTimeout(() => {
      setSummarizeText(text);
    }, 100);
  }, [
    setExpandedResourceSidebarOpen,
    setExpandedResourceSidebarTab
  ]);
  useFabricEvent("fabric-annotation-action-create", (e) => {
    if (e.detail.resourceId !== resourceId) return;
    setAnnotationData(e.detail.annotation);
    setExpandedResourceSidebarOpen(true);
    setExpandedResourceSidebarTab("comments");
  });
  const { mutate: mutateCreateComment } = useMutationCreateComment();
  const { refetch: refetchComments } = useQueryComments(resourceId, {
    enabled: false
  });
  useFabricEvent("fabric-annotation-action-focus", async (e) => {
    if (e.detail.resourceId !== resourceId || e.detail.origin === "comments") return;
    setExpandedResourceSidebarOpen(true);
    setExpandedResourceSidebarTab("comments");
    setBottomAdditionalHeight((prev) => isNumber(prev) ? Math.max(prev, 350) : 350);
    const comments = await refetchComments({
      cancelRefetch: true
    });
    const invalidCommentId = !comments.data?.some((comment) => comment.id === e.detail.commentId);
    if ((!e.detail.commentId || invalidCommentId) && !isString(e.detail.annotation) && isDefined(e.detail.annotation)) {
      const annotation = e.detail.annotation;
      mutateCreateComment({
        resourceId,
        content: "",
        annotation
      }, {
        onSuccess: (comment) => {
          dispatchFabricEvent("fabric-annotation-action-link", {
            type: "link-annotation",
            resourceId,
            commentId: comment.id,
            annotation
          });
          setFocusCommentId(comment.id);
        }
      });
    } else if (e.detail.commentId && !invalidCommentId) {
      setFocusCommentId(e.detail.commentId);
    }
  });
  useFabricEvent("fabric-assistant-action", (e) => {
    if (expandedResourceSidebarTab === "assistant") return;
    setExpandedResourceSidebarOpen(true);
    setExpandedResourceSidebarTab("assistant");
    setTimeout(() => {
      dispatchFabricEvent("fabric-assistant-action", e.detail);
    }, 50);
  });
  const value = useMemo(() => ({
    selectedExtraPanel: expandedResourceSidebarTab,
    setSelectedExtraPanel: setExpandedResourceSidebarTab,
    sidebarAnimateIn: !!expandedResourceSidebarInitTab,
    similarText,
    setSimilarText,
    sidebarOpen: overrideSidebarOpen ?? expandedResourceSidebarOpen,
    setSidebarOpen: setOverrideSidebarOpen ?? setExpandedResourceSidebarOpen,
    editTagsModalOpen,
    setEditTagsModalOpen,
    shareModalOpen,
    setShareModalOpen,
    handleOnDelete: handleOnDelete ?? noop,
    pdf,
    setPdf,
    toolbarHeight,
    setToolbarHeight,
    isEditorFocused,
    setIsEditorFocused,
    onGetSimilarItemsToText,
    onSummarizeText,
    isAnon: !user,
    hideBottomBar,
    setHideBottomBar,
    hideTopBar,
    setHideTopBar,
    pagesTextContent,
    annotationData,
    setAnnotationData,
    focusCommentId,
    setFocusCommentId,
    bottomAdditionalHeight,
    setBottomAdditionalHeight
  }), [
    expandedResourceSidebarTab,
    setExpandedResourceSidebarTab,
    expandedResourceSidebarInitTab,
    similarText,
    overrideSidebarOpen,
    expandedResourceSidebarOpen,
    setOverrideSidebarOpen,
    setExpandedResourceSidebarOpen,
    editTagsModalOpen,
    shareModalOpen,
    handleOnDelete,
    pdf,
    setPdf,
    toolbarHeight,
    isEditorFocused,
    onGetSimilarItemsToText,
    onSummarizeText,
    user,
    pagesTextContent,
    hideBottomBar,
    setHideBottomBar,
    hideTopBar,
    setHideTopBar,
    annotationData,
    setAnnotationData,
    focusCommentId,
    setFocusCommentId,
    bottomAdditionalHeight,
    setBottomAdditionalHeight
  ]);
  return /* @__PURE__ */ jsx(ExpandedFdocContext.Provider, {
    value,
    children
  });
};
const useExpandedFdocContext = () => {
  return useContext(ExpandedFdocContext);
};

export { ExpandedFdocProvider, useExpandedFdocContext };
//# sourceMappingURL=ExpandedFdocProvider.js.map
